import React, {
  useState,
  useEffect,
  useContext,
  createContext,
  useCallback,
  useRef,
} from 'react';
import { connect } from 'react-redux';
import { createConsumer, Consumer, Subscription } from '@rails/actioncable';
import getActionCableConfig from '../configs/actionCable';

interface SubscriptionHandlers {
  received?: (data: any) => void;
  connected?: () => void;
  disconnected?: () => void;
  rejected?: () => void;
}

interface ActionCableContextValue {
  consumer: Consumer | null;
  subscribe: (
    channel: string,
    params?: Record<string, any>,
    handlers?: SubscriptionHandlers,
  ) => Subscription | null;
  unsubscribe: (subscription: Subscription) => void;
  connected: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const ActionCableContext = createContext<ActionCableContextValue>({
  consumer: null,
  subscribe: () => null,
  unsubscribe: noop,
  connected: false,
});

interface User {
  id: string;
  first_name?: string;
}

interface RootState {
  auth: {
    user: User | null;
  };
}

interface StateProps {
  user: User | null;
}

function mapStateToProps(state: RootState): StateProps {
  return { user: state.auth.user };
}

interface ActionCableProviderProps extends StateProps {
  children: React.ReactNode;
}

const ActionCableProviderInner: React.FC<ActionCableProviderProps> = ({
  user,
  children,
}) => {
  const [consumer, setConsumer] = useState<Consumer | null>(null);
  const [connected, setConnected] = useState(false);
  const subscriptionsRef = useRef<Map<string, Subscription>>(new Map());

  // biome-ignore lint/correctness/useExhaustiveDependencies: consumer is intentionally excluded to prevent reconnection loops
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const authToken = localStorage.getItem('authToken');
    let newConsumer: Consumer | null = null;

    if (user && authToken && !consumer) {
      const config = getActionCableConfig();
      const url = `${config.url}?token=${encodeURIComponent(authToken)}`;

      // Detect mixed content: HTTPS page trying to connect to ws://
      const isHttpsPage = window.location.protocol === 'https:';
      const isInsecureWebSocket = config.url.startsWith('ws://');

      if (isHttpsPage && isInsecureWebSocket) {
        console.warn(
          'Action Cable: Cannot connect - page loaded over HTTPS but WebSocket URL uses ws://. ' +
            'Set NEXT_PUBLIC_API_URL to an HTTPS URL to enable real-time features.',
        );
        return;
      }

      try {
        newConsumer = createConsumer(url);
        setConsumer(newConsumer);
        setConnected(true);
        console.log('Action Cable: Connected');
      } catch (error) {
        console.warn(
          'Action Cable: Connection failed - real-time features will be unavailable',
          error,
        );
      }
    }

    // Copy ref to local variable for cleanup
    const subscriptions = subscriptionsRef.current;

    return () => {
      if (newConsumer) {
        console.log('Action Cable: Disconnecting');
        newConsumer.disconnect();
        setConsumer(null);
        setConnected(false);
        subscriptions.clear();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (consumer) {
        consumer.disconnect();
      }
    };
  }, [consumer]);

  const subscribe = useCallback(
    (
      channelName: string,
      params: Record<string, any> = {},
      handlers: SubscriptionHandlers = {},
    ): Subscription | null => {
      if (!consumer) return null;

      const key = `${channelName}-${JSON.stringify(params)}`;

      // Avoid duplicate subscriptions
      if (subscriptionsRef.current.has(key)) {
        return subscriptionsRef.current.get(key)!;
      }

      try {
        const subscription = consumer.subscriptions.create(
          { channel: channelName, ...params },
          {
            received: handlers.received || noop,
            connected: () => {
              console.log(`Action Cable: Subscribed to ${channelName}`);
              handlers.connected?.();
            },
            disconnected: () => {
              console.log(`Action Cable: Disconnected from ${channelName}`);
              handlers.disconnected?.();
            },
            rejected: () => {
              console.warn(
                `Action Cable: Subscription to ${channelName} rejected`,
              );
              handlers.rejected?.();
            },
          },
        );

        subscriptionsRef.current.set(key, subscription);
        return subscription;
      } catch (error) {
        console.warn(
          `Action Cable: Failed to subscribe to ${channelName}. ` +
            'This may be due to mixed content (HTTPS page with ws:// WebSocket). ' +
            'Real-time features will be unavailable.',
          error,
        );
        return null;
      }
    },
    [consumer],
  );

  const unsubscribe = useCallback((subscription: Subscription) => {
    if (!subscription) return;
    subscription.unsubscribe();

    // Remove from tracking
    for (const [key, sub] of subscriptionsRef.current.entries()) {
      if (sub === subscription) {
        subscriptionsRef.current.delete(key);
        break;
      }
    }
  }, []);

  return (
    <ActionCableContext.Provider
      value={{ consumer, subscribe, unsubscribe, connected }}
    >
      {children}
    </ActionCableContext.Provider>
  );
};

const ActionCableProvider = connect(mapStateToProps)(ActionCableProviderInner);

export default ActionCableProvider;

export const useActionCable = () => useContext(ActionCableContext);
